/**
 * vue-i18n Plugin Setup — Donor Portal
 *
 * Configures vue-i18n (official Vue 3 i18n plugin) with:
 * - English messages loaded eagerly as the fallback bundle from @tms-phoenix/tms-types
 * - On-the-fly lazy-loading of other locale JSON files (de, tr)
 *   from packages/tms-types/src/locales via Vite glob imports
 * - localStorage persistence for the active language
 * - Pinia locale store as the single source of truth (no duplicate API calls)
 */
import { createI18n } from 'vue-i18n';
import type { App } from 'vue';

import { en as enFallbackBundle } from '@tms-phoenix/tms-types';

// ── Constants ───────────────────────────────────────────────────────────────

export const DEFAULT_LANGUAGE = 'en';

export const SUPPORTED_LANGUAGES = ['en', 'de', 'tr'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_STORAGE_KEY = 'tms-locale-lang';

// ── Flat → Nested key converter ─────────────────────────────────────────────
//
// The tms-types locale JSON files use flat dotted keys: { "common.home": "Home" }.
// vue-i18n expects nested objects:                      { common: { home: "Home" } }.
// This utility converts on load so that t('common.home') resolves correctly.
//
// It also normalises i18next-style double-brace placeholders ({{var}}) to the
// vue-i18n single-brace form ({var}) so existing translation strings resolve
// without a mass find-and-replace of every locale JSON file.

function normalizeInterpolation(value: string): string {
  return value.replace(/\{\{(\s*[\w.]+\s*)\}\}/g, '{$1}');
}

function flatToNested(flat: Record<string, string>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.');
    let current = result;
    for (let i = 0; i < parts.length - 1; i++) {
      if (typeof current[parts[i]] !== 'object' || current[parts[i]] === null) {
        current[parts[i]] = {};
      }
      current = current[parts[i]] as Record<string, unknown>;
    }
    current[parts[parts.length - 1]] = normalizeInterpolation(value);
  }
  return result;
}

// ── Lazy locale loaders — Vite glob (resolved at build time) ─────────────────
//
// Vite creates a separate async chunk per locale file. Only the chunk for the
// active language is fetched at runtime — no redundant network requests.
//
// The @tms-locales alias is defined in vite.config.mts → resolve.alias and
// points to packages/tms-types/src/locales, making this path depth-independent.

const localeModules = import.meta.glob<{ default: Record<string, string> }>(
  '@tms-locales/*.json',
  { eager: false },
);

/** Track which locales are already registered to avoid duplicate work. */
const loadedLocales = new Set<string>([DEFAULT_LANGUAGE]);

// ── vue-i18n singleton ──────────────────────────────────────────────────────

export const i18n = createI18n({
  legacy: false, // Composition API mode — useI18n() instead of this.$t
  locale: DEFAULT_LANGUAGE,
  fallbackLocale: DEFAULT_LANGUAGE,
  messages: {
    en: flatToNested(enFallbackBundle as unknown as Record<string, string>),
  } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
});

// ── Public helpers ──────────────────────────────────────────────────────────

/** Lazy-loads the locale bundle for `lang` if it has not been loaded yet. */
export async function loadLocaleMessages(lang: SupportedLanguage): Promise<void> {
  if (loadedLocales.has(lang)) return;

  const key = Object.keys(localeModules).find((k) => k.endsWith(`/${lang}.json`));
  if (!key) {
    console.warn(`[i18n] No locale file for "${lang}". Falling back to "${DEFAULT_LANGUAGE}".`);
    return;
  }

  const mod = await localeModules[key]();
  i18n.global.setLocaleMessage(lang, flatToNested(mod.default));
  loadedLocales.add(lang);
}

/** Lazy-loads `lang`'s bundle then switches the active locale. */
export async function changeLanguage(lang: SupportedLanguage): Promise<void> {
  await loadLocaleMessages(lang);
  (i18n.global.locale as unknown as { value: string }).value = lang;
  localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
  document.documentElement.dir = rtlLanguages.some((rtl) => lang.startsWith(rtl)) ? 'rtl' : 'ltr';
  document.documentElement.lang = lang;
}

/** Returns the currently active language code. */
export function getCurrentLanguage(): string {
  return (i18n.global.locale as unknown as { value: string }).value || DEFAULT_LANGUAGE;
}

// ── No-op backward-compat stubs (locale.store.ts references these) ──────────

/** @deprecated No-op — localStorage bundle caching is no longer used. */
export function clearBundleCache(): void { /* empty */ }

/** @deprecated No-op — tenant context is not used in the donor portal. */
export function clearTenantContext(): void { /* empty */ }

/** @deprecated No-op — bundles are loaded directly from tms-types locale files. */
export async function reloadTranslationsPostAuth(): Promise<void> { /* empty */ }

// ── Vue plugin installer ────────────────────────────────────────────────────

/**
 * Registers vue-i18n on the Vue app instance.
 * If the user previously selected a non-English locale, the bundle is lazy-loaded
 * before the app mounts so the first render is already localised.
 */
export async function setupI18n(app: App): Promise<void> {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  const initial = (
    stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage) ? stored : DEFAULT_LANGUAGE
  ) as SupportedLanguage;

  if (initial !== DEFAULT_LANGUAGE) {
    await changeLanguage(initial);
  }

  app.use(i18n);
}
